import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Layout, Layouts, Responsive} from 'react-grid-layout';
import {useDispatch, useSelector} from 'react-redux';

import {RootState} from 'appredux/reducers';
import {rdwSetWidgetLayout, rdwUpdateWidgetSize} from 'appredux/actions';

import type {WdgId} from './index';
import {shouldShowOverviewWidget, widgetComponentMap} from './index';
import {reduxSnapItem} from './snapToZones';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import WidgetFrame from './WidgetFrame';
import {apiGetWidgets, apiReplaceSupplierWidgets} from 'constants/Api';
import {IWidgetInput} from 'constants/interfaces';

const MIN_GRID_WIDTH = 320;
const isKnownWidgetId = (name: unknown): name is WdgId =>
  typeof name === 'string' && name in widgetComponentMap;

/* -------- types -------- */
export interface IGridItem extends Layout {
  i: WdgId;
}

export default function WidgetWorkspace(): React.ReactElement {
  /* 🔑 corrected state slice */
  const {layout} = useSelector((s: RootState) => s.reduxDashboardWidgets);
  const user = useSelector((s: RootState) => s.user);
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gridWidth, setGridWidth] = useState(0);
  const visibleLayout = useMemo(
    () =>
      layout.filter(
        (item) =>
          isKnownWidgetId(item.i) && shouldShowOverviewWidget(item.i, user),
      ),
    [layout, user],
  );

  /* first paint – snap once so UI ≙ store */
  const layouts = useMemo<Layouts>(
    () => ({lg: visibleLayout.map(reduxSnapItem)}),
    [visibleLayout],
  );

  const [loadingLayout, setLoadingLayout] = useState(true);

  /* helper (snap → dispatch) */
  const saveLayout = useCallback(
    (items: Layout[]) => {
      if (loadingLayout) return;
      const snappedLayout = items.map(reduxSnapItem) as IGridItem[];
      apiReplaceSupplierWidgets(
        snappedLayout.map(
          (item) =>
            ({
              name: item.i as WdgId,
              x: item.x,
              y: item.y,
              width: item.w,
              height: item.h,
            } as IWidgetInput),
        ),
      ).catch((error) => {
        alert(
          'Failed to save widget layout at the moment. Please try again later.',
        );
        console.error('Failed to save widget layout:', error);
      });
      dispatch(rdwSetWidgetLayout(snappedLayout));
    },
    [dispatch, loadingLayout],
  );

  useEffect(() => {
    apiGetWidgets()
      .then((widgets) => {
        if (!widgets || !widgets.length) {
          console.info('No widgets saved, using default layout');
          return;
        }
        const items = widgets
          .filter(
            (widget) =>
              isKnownWidgetId(widget.name) &&
              shouldShowOverviewWidget(widget.name, user),
          )
          .map(
            (widget) =>
              ({
                i: widget.name as WdgId,
                x: widget.x,
                y: widget.y,
                w: widget.width,
                h: widget.height,
              } as IGridItem),
          );
        dispatch(rdwSetWidgetLayout(items.map(reduxSnapItem) as IGridItem[]));
      })
      .finally(() => setLoadingLayout(false));
  }, [dispatch, user]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    let frame = 0;
    const measure = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const measuredElement = element.parentElement ?? element;
        const nextWidth = Math.floor(
          measuredElement.getBoundingClientRect().width,
        );

        setGridWidth((currentWidth) => {
          if (nextWidth < MIN_GRID_WIDTH) return currentWidth;
          if (Math.abs(nextWidth - currentWidth) < 2) return currentWidth;
          return nextWidth;
        });
      });
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      if (element.parentElement) observer.observe(element.parentElement);
      return () => {
        observer.disconnect();
        window.cancelAnimationFrame(frame);
      };
    }

    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  /* live drag */
  const handleLayoutChange = useCallback(
    (_cur: Layout[], all: Layouts) => {
      if (!all.lg) return;
      saveLayout(all.lg);
    },
    [saveLayout],
  );

  /* drop */
  const handleDragStop = useCallback(
    (lg: Layout[]) => saveLayout(lg),
    [saveLayout],
  );

  return (
    <div className="widget-grid-shell" ref={containerRef}>
      {gridWidth >= MIN_GRID_WIDTH ? (
        <Responsive
          key={gridWidth}
          className="widget-grid"
          breakpoints={{lg: 1200}}
          cols={{lg: 9}}
          rowHeight={32}
          layouts={layouts}
          margin={[14, 14]}
          preventCollision={false}
          compactType="vertical"
          isResizable={false}
          draggableHandle=".drag-handle"
          onLayoutChange={handleLayoutChange}
          onDragStop={handleDragStop}
          width={gridWidth}
        >
          {visibleLayout.map((g) => {
            const Widget = widgetComponentMap[g.i];
            if (!Widget) return null;

            return (
              <div key={g.i} data-grid={reduxSnapItem(g)}>
                <WidgetFrame
                  id={g.i}
                  onResize={(w: 4 | 5 | 9) =>
                    dispatch(rdwUpdateWidgetSize(g.i, w))
                  }
                >
                  <Widget />
                </WidgetFrame>
              </div>
            );
          })}
        </Responsive>
      ) : null}
    </div>
  );
}
